import { createDatapointForSelectionBox } from '../components/autocode/AutocodeFunctions';
import { AC_1_PLUS_USERS_SUFFIX } from '../components/autocode/AutocodeConstants';

// Drag and Drop util for Template 1.1,3,5,9
export const getDatapointsForSingleList = (
  selectedItems,
  selectedNodeAnswers
) => {
  const selectedAnswers = selectedItems.filter(i => i.checked);

  // create datapoint shape
  const datapoints = selectedAnswers.map(ans =>
    createDatapointForSelectionBox(selectedNodeAnswers, ans)
  );
  return datapoints;
};

// Drag and Drop util for Template 4
export const getDatapointsForTemplate4 = (
  selectedItems,
  selectedNodeAnswers,
  userOptions,
  selectedUserOption
) => {
  const selectedAnswers = selectedItems.filter(i => i.checked);

  // create datapoint shape
  const datapoints = selectedAnswers
    .map(ans => createDatapointForSelectionBox(selectedNodeAnswers, ans))
    .map(dp => {
      if (selectedUserOption === '1+-users') {
        return {
          ...dp,
          keyword: `${dp.keyword} ${AC_1_PLUS_USERS_SUFFIX}`, // update expression in keyword with special suffix
        };
      }
      return dp;
    })
    .map(dp => {
      const tpArr = dp.textPath.split('.');
      return {
        ...dp,
        uniqueId: `${dp.uniqueId}_${selectedUserOption}`,
        // selected user option label should be 2nd to last label
        labels: [
          ...dp.labels.slice(0, dp.labels.length - 2),
          userOptions.find(u => u.value === selectedUserOption).label,
          ...dp.labels.slice(dp.labels.length - 1),
        ],
        // selected user option value should be 2nd to last in text path
        textPath: [
          ...tpArr.slice(0, tpArr.length - 2),
          selectedUserOption,
          tpArr[tpArr.length - 1],
        ].join('.'),
      };
    });

  return datapoints;
};

/* The below constants are created for getDatapointsForTemplate1Category
  and getDatapointsForTemplate1BrandTypeKindForm util functions */
// different types of items in the list
const itemType = {
  TOTAL_USERS: 1,
  RECENT_USERS: 2,
  HML: 3,
  FROM_TO: 4,
  EXACTLY: 5,
  PRE_DEFINED_RANGE: 6,
};

//  replace volume with users
const replaceVolumewithUsers = dp => {
  const d = dp.labels[dp.labels.length - 2];
  if (d.toLowerCase() === 'volume') {
    const label = [...dp.labels];
    label[label.length - 2] = 'Users';
    return label;
  }

  return dp.labels;
};

// valid range item has...
// from, to are numbers
// from <= to
// from, to >= 0
const isValidRange = rangeItem => {
  const allDigitsRegex = /^\d+$/;
  return (
    allDigitsRegex.test(rangeItem.from.trim()) &&
    allDigitsRegex.test(rangeItem.to.trim()) &&
    Number(rangeItem.from) <= Number(rangeItem.to) &&
    Number(rangeItem.from) >= 0 &&
    Number(rangeItem.to) >= 0
  );
};

// get volume numbers in range between start and end exclusive
// if end is undefined get numbers between start and end of list
const getVolumeNumbersInRange = (start, end, volumeNumbers) => {
  return volumeNumbers.filter(num => {
    const { shortText } = num;
    // values in current volume number shortText
    // 1 (n1: 1)
    // 2-3 (n1: 2, n2: 3)
    // 4+ (n1: 4)
    const [n1, n2] = shortText.match(/\d+/g).map(n => Number(n));

    // if volume number is an open range (4+)
    // include if start or end are greater than n1
    if (shortText.includes('+') && (n1 <= start || n1 <= end)) return true;

    // if volume number is closed range (2-3)
    // include if start or end are in the range
    if (
      shortText.includes('-') &&
      ((start >= n1 && start <= n2) || (end >= n1 && end <= n2))
    )
      return true;

    // if volume number is single value (1)
    // include if start or end match it...
    if (n1 === start || n1 === end) return true;

    // or if it is after start without an end...
    if (end === undefined && start <= n1) return true;

    // or if it is in between start and end
    if ((start <= n1 && n1 <= end) || (start <= n2 && n2 <= end)) return true;

    return false;
  });
};

// volume keyword for an item depends on item type
// and by parsing its shortText
const getVolumeKeywordForItem = (
  item,
  totalItem,
  selectedUserOption,
  volumeNumbers
) => {
  const { shortText: label, type } = item;

  let start;
  let end;

  switch (type) {
    // Total Users: take all starting at 1
    case itemType.TOTAL_USERS: {
      if (totalItem) {
        return `${selectedUserOption} AND ${totalItem.keyword}`;
        // return keyword.replace(/ >= \d+/g, '');
      }
      start = 1;
      break;
    }
    // Recent Users: take all starting at from value
    // if total volume exists keyword will contain > symbol
    // remove '>= num' part from keyword and return as volKeyword
    case itemType.RECENT_USERS: {
      if (totalItem) {
        return `${selectedUserOption} AND ${totalItem.keyword}`;
        // return keyword.replace(/ >= \d+/g, '');
      }
      const { from } = item;
      start = Number(from);
      break;
    }
    case itemType.HML: {
      if (totalItem) {
        return `${item.keyword} AND ${totalItem.keyword}`
      }

      // parse label for start/end values
      const parsed = label.match(/\d+/g).map(n => Number(n));
      // Heavy: take all starting at first parsed value
      if (label.includes('Heavy')) {
        start = parsed[0];
      }
      // Medium: take all between first and second parsed values
      if (label.includes('Medium')) {
        start = parsed[0];
        end = parsed[1];
      }
      // Light: take all between first and second parsed values
      // if first value is 0, make it 1
      if (label.includes('Light')) {
        start = parsed[0] === 0 ? 1 : parsed[0];
        end = parsed[1];
      }
      break;
    }
    // From-to:, take all between from and to values
    // if to is MAX or to < from, set to undefined
    case itemType.FROM_TO: {
      const { from, to } = item;
      start = Number(from);
      end = to !== 'MAX' && Number(from) <= Number(to) ? Number(to) : undefined;
      break;
    }
    // Exactly: start and end are both from value
    case itemType.EXACTLY: {
      const { from } = item;
      start = Number(from);
      end = Number(from);
      break;
    }
    // Predefined range: take all between the two parsed values
    // if second value doesn't exist set start end end to first value
    case itemType.PRE_DEFINED_RANGE: {
      const parsed = label.match(/\d+/g).map(n => Number(n));
      start = parsed[0];
      end = parsed[1] || parsed[0];
      break;
    }
    default:
      break;
  }

  // get volume numbers in range
  const volumeNumbersInRange = getVolumeNumbersInRange(
    start,
    end,
    volumeNumbers
  );

  // get keywords/midpoints of volume numbers
  const keywordsAndMidpoints = volumeNumbersInRange.reduce(
    (a, { keyword, midpoint }) => a.concat(keyword, midpoint),
    []
  );

  // Join keywords/midpoints with comma and wrap with VOLUME
  return `VOLUME (${keywordsAndMidpoints.join(
    ', '
  )}) AND ${selectedUserOption}`;
};

// keyword for item depends on the item type
const getKeywordForItem = (
  item,
  totalItem,
  selectedUserOption,
  totalUsersItem,
  volumeNumbers
) => {
  switch (item.type) {
    case itemType.TOTAL_USERS: {
      return selectedUserOption;
    }
    case itemType.RECENT_USERS: {
      const { from } = item;
      if (totalItem) {
        return `${selectedUserOption} AND ${totalItem.keyword} >= ${from}`;
      }
      const volNums = getVolumeNumbersInRange(
        Number(from),
        undefined,
        volumeNumbers
      );
      return `(${volNums
        .map(n => n.keyword)
        .join(' OR ')}) AND ${selectedUserOption}`;
    }
    case itemType.HML: {
      return item.keyword;
    }
    case itemType.FROM_TO: {
      const { from, to } = item;
      // if from value is blank, default to 0
      const defaultFrom = from.length === 0 ? '0' : from;
      // if total volume keyword exists use it the keyword expression
      if (totalItem) {
        const { keyword } = totalItem;
        return !isValidRange(item)
          ? `(${keyword} >= ${defaultFrom}) AND ${totalUsersItem.keyword}`
          : `((${keyword} >= ${defaultFrom}) AND (${keyword} <= ${to})) AND ${totalUsersItem.keyword}`;
      }
      // otherwise use volume numbers in range seperated by OR
      const volNums =
        to === 'MAX'
          ? getVolumeNumbersInRange(
            Number(defaultFrom),
            undefined,
            volumeNumbers
          )
          : getVolumeNumbersInRange(
            Number(defaultFrom),
            Number(to),
            volumeNumbers
          );
      return volNums.map(n => n.keyword).join(' OR ');
    }
    case itemType.EXACTLY: {
      const { from } = item;
      // if from value is blank, default to 0
      const defaultFrom = from.length === 0 ? '0' : from;
      // if total volume keyword exists use it the keyword expression
      if (totalItem) {
        return `(${totalItem.keyword} = ${defaultFrom}) AND ${totalUsersItem.keyword}`;
      }
      // otherwise use volume numbers in range seperated by OR
      const volNums = getVolumeNumbersInRange(
        Number(defaultFrom),
        Number(defaultFrom),
        volumeNumbers
      );
      return volNums.map(n => n.keyword).join(' OR ');
    }
    case itemType.PRE_DEFINED_RANGE: {
      return item.keyword;
    }
    default:
      return '';
  }
};

// Drag and Drop util for Template1Category
export const getDatapointsForTemplate1Category = (
  selectedItems,
  selectedNodeAnswers,
  userOptions,
  selectedUserOption,
  totalUsersItem
) => {
  // Adding these Constant as it was required for addtosandbox function in respective component
  const volumeItems = selectedNodeAnswers.root.children.find(c =>
    c.children.some(cc => cc.shortText === 'Volume')
  );
  const volumeNumbers = volumeItems.children[0].children.filter(
    c =>
      !c.shortText.includes('Total') &&
      !c.shortText.includes('Heavy') &&
      !c.shortText.includes('Medium') &&
      !c.shortText.includes('Light')
  );
  const totalItem = volumeItems.children[0].children.find(
    c => c.shortText === 'Total'
  );
  // find selected non range items
  const selectedAnswers = selectedItems
    .filter(i => i.checked && !i.rangeItem)
    .map(i => ({
      ...i,
      keyword: getKeywordForItem(
        i,
        totalItem,
        selectedUserOption,
        totalUsersItem,
        volumeNumbers
      ),
      volKeyword: getVolumeKeywordForItem(
        i,
        totalItem,
        selectedUserOption,
        volumeNumbers
      ),
      shortText: `${i.shortText} ${i.addedText}`,
      uniqueId: `${i.pathId}_${selectedUserOption}_${i.shortText}`,
    }));

  // find selected range items
  // change shortText and keyword for 'from to' and 'exactly' items
  const selectedRangeAnswers = selectedItems
    .filter(i => i.checked && i.rangeItem)
    .map(i => {
      // if from value is blank, default to 0
      const defaultFrom = i.from.length === 0 ? '0' : i.from;
      const keyword = getKeywordForItem(
        i,
        totalItem,
        selectedUserOption,
        totalUsersItem,
        volumeNumbers
      );
      const volKeyword = getVolumeKeywordForItem(
        i,
        totalItem,
        selectedUserOption,
        volumeNumbers
      );
      if (i.node === 'fromTo') {
        // append from, to values to shortText
        const shortText = !isValidRange(i)
          ? `${i.shortText}_${defaultFrom}+ ${i.addedText}`
          : `${i.shortText}_${i.from}-${i.to} ${i.addedText}`;

        return {
          ...i,
          shortText,
          keyword,
          volKeyword,
          uniqueId: `${i.pathId}_${selectedUserOption}_${shortText}`,
        };
      }
      return {
        ...i,
        // append from value to short text
        shortText: `${i.shortText}_${defaultFrom} ${i.addedText}`,
        keyword,
        volKeyword,
        uniqueId: `${i.pathId}_${selectedUserOption}_${i.shortText}_${defaultFrom}`,
      };
    });

  // create datapoints for them
  const datapoints = [...selectedAnswers, ...selectedRangeAnswers]
    .map(ans => createDatapointForSelectionBox(selectedNodeAnswers, ans))
    .map(dp => {
      const tpArr = dp.textPath.split('.');
      const label = replaceVolumewithUsers(dp);
      return {
        ...dp,
        // if volKeyword exists with VOL fn make datatype numeric
        // this is a hack to make AutcodeLabelGenerator play nice with VOLUME
        datatype:
          dp.volKeyword && dp.volKeyword.includes('VOLUME') ? 'N' : dp.datatype,
        // time period for template 1 should appear in selection box function labels
        // append it to the datapoints being sent to selection box
        timePeriod: volumeItems.shortText, // time period is short text of volume items
        // selected user option label should be 3rd to last label
        labels: [
          ...label.slice(0, label.length - 3),
          userOptions.find(u => u.value === selectedUserOption).label,
          ...label.slice(label.length - 2),
        ],
        // selected user option value should be 3rd to last in text path
        textPath: [
          ...tpArr.slice(0, tpArr.length - 3),
          selectedUserOption,
          ...dp.labels.slice(dp.labels.length - 2),
        ].join('.'),
      };
    });

  return datapoints;
};

// Drag and Drop util for Template1BrandTypeKindForm
export const getDatapointsForTemplate1BrandTypeKindForm = (
  selectedItems,
  selectedNodeAnswers,
  userOptions,
  selectedUserOption,
  onePlusSelected,
  onePlusValue
) => {
  // find all selected answers that have the selected user option in children
  let selectedAnswers;
  if (selectedUserOption === 'Total Users 1+') {
    selectedAnswers = selectedItems
      .filter(i => i.checked)
      .filter(i => i.children.some(c => c.shortText === 'Total Users'))
      .map(i => i.children.find(c => c.shortText === 'Total Users'));
  } else {
    selectedAnswers = selectedItems
      .filter(i => i.checked)
      .filter(i => i.children.some(c => c.shortText === selectedUserOption))
      .map(i => i.children.find(c => c.shortText === selectedUserOption));
  }
  // create datapoint shape
  let volumeKeyword;

  // not sure about this...
  // find list of brands that do not have Volume as answers
  const nonVolumeItems = selectedNodeAnswers.root.children.find(c =>
    c.children[0].children.some(cc => cc.shortText !== 'Volume')
  );

  const volumeItems = selectedNodeAnswers.root.children.find(c =>
    c.children[0].children.some(cc => cc.shortText === 'Volume')
  );
  const datapoints = selectedAnswers
    .map(ans => createDatapointForSelectionBox(selectedNodeAnswers, ans))
    // flip the position of the last and second to last labels and textPath
    // this looks better in the selection box
    .map(dp => {
      const tpArr = dp.textPath.split('.');
      let volKeyword = null;
      let keyword = null;
      if (volumeItems && volumeItems.children) {
        // find answer of dp in nonVolumeItems
        const answer = nonVolumeItems.children.find(a =>
          a.children.some(c => dp.keyword.includes(c.keyword))
        );
        // find corresponding volume answer via keyword
        const volumeAnswer = volumeItems.children.find(
          c => c.keyword === answer.keyword
        ).children[0];
        volumeKeyword = volumeAnswer.keyword;
      }
      // keyword expression for onePlus option...
      // 2:
      // volKeyword: volumeKeyword = 2 AND volumeKeyword
      // keyword: volumeKeyword = 2
      // 1+:
      // volKeyword: volumeKeyword >= 1 AND volumeKeyword
      // keyword: volumeKeyword >= 1
      // 1-2:
      // volKeyword: volumeKeyword >= 1 AND volumeKeyword <= 2 AND volumeKeyword
      // keyword: volumeKeyword >= 1 AND volumeKeyword <= 2
      if (
        onePlusSelected &&
        !onePlusValue.includes('+') &&
        !onePlusValue.includes('-')
      ) {
        volKeyword = `${volumeKeyword} = ${onePlusValue} AND ${volumeKeyword}`;
        keyword = `${volumeKeyword} = ${onePlusValue}`;
      } else if (onePlusSelected && onePlusValue.includes('+')) {
        volKeyword = `${volumeKeyword} >= ${onePlusValue.replace(
          '+',
          ''
        )} AND ${volumeKeyword}`;
        keyword = `${volumeKeyword} >= ${onePlusValue.replace('+', '')}`;
      } else if (onePlusSelected && onePlusValue.includes('-')) {
        const [start, end] = onePlusValue.match(/\d+/g);
        volKeyword = `(${volumeKeyword} >= ${start} AND ${volumeKeyword} <= ${end}) AND ${volumeKeyword}`;
        keyword = `(${volumeKeyword} >= ${start} AND ${volumeKeyword} <= ${end})`;
      } else {
        volKeyword = `${dp.keyword} AND ${volumeKeyword}`;
        keyword = dp.keyword;
      }
      return {
        ...dp,
        uniqueId: onePlusSelected
          ? `${dp.uniqueId}.${onePlusValue}`
          : `${dp.uniqueId}.${selectedUserOption}`,
        volKeyword,
        keyword,
        // since the non-volume items are used for the selected items list the datatype is always B
        // if there are volume items (doublebase studies) set the datapoint to be N
        datatype: volumeItems ? 'N' : dp.datatype,
        // time period for template 1 should appear in selection box function labels
        // append it to the datapoints being sent to selection box
        timePeriod: volumeItems ? volumeItems.shortText : '', // time period is short text of volume items
        labels: [
          ...dp.labels.slice(0, dp.labels.length - 3),
          userOptions.find(u => u.value === selectedUserOption).label,
          dp.labels[dp.labels.length - 2],
        ],
        textPath: [
          ...tpArr.slice(0, tpArr.length - 3),
          selectedUserOption,
          tpArr[tpArr.length - 2],
        ].join('.'),
      };
    });

  return datapoints;
};

// Drag and Drop util for Template 2,7,8
export const getDatapointsForTemplate278 = (
  child,
  selectedNode,
  selectedNodeAnswers
) => {
  // data is set in each node and children
  const list = selectedNode.map(res => {
    if (res.checked) {
      child.map(a => {
        if (a.checked) {
          res.children = res.children.map(res => {
            if (res.shortText === a.shortText) {
              return { ...res, checked: true };
            }
            return { ...res };
          });
        }
        return { ...a };
      });
    }
    return res;
  });
  // Commented as this fucntion is used in use effect
  // setSelectedNode(list);
  //  only selected values
  const selectedAnswers = list
    .filter(i => i.checked)
    .map(f => f.children.filter(c => c.checked));
  const selectedAns = selectedAnswers.map(res =>
    res.map(ans => createDatapointForSelectionBox(selectedNodeAnswers, ans))
  );

  return selectedAns;
};
